import React from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  IS_EUROPE, BRAND, SITE_URL, LANGUAGES, LANGUAGE, LANGUAGE_CODES, DEFAULT_LANGUAGE, localeUrl,
} from '../config/region';

/*
 * Per-page SEO head.
 *
 * The language prefix is what makes this worth having: because /en/blog and /de/blog are distinct
 * URLs, each language can carry its own canonical and its own title, and the set of them can be
 * declared to a crawler as translations of one another via hreflang. That is not expressible when
 * one URL renders differently depending on what the visitor has in localStorage — which is what
 * the site did before, and why only one language was ever indexed.
 *
 *   canonical  — this page, at this language's URL. Self-referencing, never cross-language.
 *   alternate  — the same page in each of the other languages, plus x-default for an
 *                unprefixed entry point, which redirects on the visitor's own preference.
 *
 * `path` overrides the current location for the rare page that wants a canonical other than the
 * URL it was reached at (a play link, say, which has query-string variants).
 */
const SeoHead = ({
  title,
  description,
  path,
  image = `${SITE_URL}/logo512.png`,
  imageAlt,
  type = 'website',
  jsonLd,
  noindex = false,
  children,
}) => {
  const location = useLocation();
  /* useLocation() is already relative to the router basename, so it is the language-free path */
  const relativePath = path || location.pathname;
  const canonical = localeUrl(relativePath);

  /* Several authored seoTitles already end in the brand ("… — AI Novel"); appending it again
     would produce "… — AI Novel — AI Novel" in the tab and in search results. */
  const fullTitle = !title ? BRAND : (title.includes(BRAND) ? title : `${title} — ${BRAND}`);
  const ogLocale = `${LANGUAGE_CODES[LANGUAGE]}_${LANGUAGE_CODES[LANGUAGE].toUpperCase()}`;

  return (
    <Helmet>
      <html lang={LANGUAGE_CODES[LANGUAGE]} />
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <link rel="canonical" href={canonical} />
      {/* Only the europe build has translations to point at; russia is a single-language site. */}
      {IS_EUROPE && LANGUAGES.map(({ code }) => (
        <link
          key={code}
          rel="alternate"
          hrefLang={LANGUAGE_CODES[code]}
          href={localeUrl(relativePath, code)}
        />
      ))}
      {IS_EUROPE && (
        <link rel="alternate" hrefLang="x-default" href={localeUrl(relativePath, DEFAULT_LANGUAGE)} />
      )}

      <meta property="og:site_name" content={BRAND} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={ogLocale} />
      {IS_EUROPE && LANGUAGES.filter(l => l.code !== LANGUAGE).map(({ code }) => (
        <meta
          key={code}
          property="og:locale:alternate"
          content={`${LANGUAGE_CODES[code]}_${LANGUAGE_CODES[code].toUpperCase()}`}
        />
      ))}
      <meta property="og:image" content={image} />
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}

      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
      {children}
    </Helmet>
  );
};

export default SeoHead;
